import { useEffect } from 'react'
import { useAccountPnl } from '../../../hooks/useAccountPnl.js'

export function AccountPnlPanel({ accountId, symbol, canViewPnl, refreshKey = 0 }) {
  const { data, error, busy, connected, reload } = useAccountPnl(canViewPnl ? accountId : null, symbol)

  useEffect(() => {
    if (refreshKey > 0 && canViewPnl && accountId) void reload({ sync: true })
  }, [refreshKey, canViewPnl, accountId, reload])

  if (!canViewPnl) return null

  const positions = Array.isArray(data?.positions) ? data.positions : []
  return (
    <div className="mt-2 rounded-xl border border-slate-200/80 bg-white/70 p-2.5 text-[0.75rem] dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">PNL</span>
        <span className={`text-[0.7rem] ${connected ? 'text-emerald-500' : 'text-slate-400'}`}>
          {busy ? 'syncing' : connected ? 'live' : 'poll'}
        </span>
      </div>
      {error ? <p className="mb-2 text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
        <Metric label="Net" value={data?.netPnl} signed />
        <Metric label="ROI" value={formatPct(data?.roi)} signed />
        <Metric label="Realized" value={data?.realizedPnl} signed />
        <Metric label="Unreal." value={data?.unrealizedPnl} signed />
        <Metric label="Fees" value={data?.fees} fee />
        <Metric label="Funding" value={data?.fundingPnl} signed />
      </div>
      {positions.length ? (
        <div className="mt-2 space-y-1 border-t border-slate-200/70 pt-2 dark:border-white/10">
          {positions.slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 font-mono">
              <span className="min-w-0 truncate text-slate-500">
                {p.symbol} {Number(p.quantity) >= 0 ? 'Long' : 'Short'}
              </span>
              <span className={tone(p.netPnl)}>{fmt(p.netPnl)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function Metric({ label, value, signed = false, fee = false }) {
  const v = fee && Number(value || 0) > 0 ? `-${fmt(value)}` : value == null ? '—' : fmt(value)
  return (
    <>
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${signed || fee ? tone(fee ? -Math.abs(Number(value || 0)) : value) : ''}`}>{v}</span>
    </>
  )
}

function fmt(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatPct(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return `${(n * 100).toFixed(2)}%`
}

function tone(value) {
  const n = Number(value)
  if (n > 0) return 'text-emerald-600 dark:text-emerald-400'
  if (n < 0) return 'text-red-600 dark:text-red-400'
  return 'text-slate-500 dark:text-slate-400'
}
